import React from "react";
import { formatNumber } from "../../utils/functions/formatNumber";
import { equalsZero } from "../functions/equalsZero";
import { getMonthText } from "../functions/getMonthText";

/**
 * pais: current pais
 * status: incremento/ disminucion de la conversacion
 * increment: %total de conversaciones
 * statusComparativo: debajo/arriba del nivel registrado
 * cifra: % migracion
 */
export const migTweets = {
  boxTitle: "Conversación sobre migración en Twitter",
  dataSubtitle: (isMostRecentYear) => {
    return isMostRecentYear
      ? "Número de tweets sobre migración"
      : "Promedio mensual de tweets sobre migración";
  },
  description: (isMostRecentYear, value, time, change) => {
    let comparisonSentence;
    comparisonSentence = equalsZero(change)
      ? "Esto implica que no hubo un cambio significativo respecto al " +
        (isMostRecentYear ? "mes" : "año") +
        " anterior."
      : "Esto significó " +
        (change < 0 ? "una disminución" : "un incremento") +
        " con respecto al " +
        (isMostRecentYear ? "mes" : "año") +
        " anterior de " +
        (change ?? "--") +
        "%.";
    return (
      <span>
        Al analizar el volumen de tweets podemos tener una idea de qué tan
        prevalente es el tema a lo largo del tiempo. Los tweets relacionados con
        el tema migratorio contabilizaron {value ? formatNumber(value) : "--"}{" "}
        en {isMostRecentYear ? getMonthText(time, "es", true) : time}.{" "}
        {comparisonSentence}
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        Para recolectar tweets relacionados con el tema migratorio, se definió
        una serie de queries que capturan los contenidos migratorios. El
        conjunto de tweets provenientes de estos queries fue filtrado para
        excluir contenidos relacionados con el tema pero que no pertenezcan a la
        región, así como falsos positivos que no estén relacionados con el tema.
        Para asignar una ubicación geográfica a cada tweet (país o ciudad), se
        utilizó la ubicación que proveen los usuarios de Twitter, ya sea como
        punto geográfico (latitud y longitud) o como el nombre de la ciudad en
        la que está registrada la cuenta. Debido a que no todos los tweets se
        pueden geo-localizar, el número total de tweets asignados a cada país o
        ciudad es, por tanto, menor al volumen real emitido en cada geografía.
        Esta metodología estandarizada para todas las geografías analizadas
        permite sin embargo hacer una comparación entre países. El proceso de
        geo-localización se estandarizó utilizando la API de{" "}
        <a
          href="https://www.here.com/?cid=Brand-PureBrand-Google-YT-0-Here-AMER-T1&utm_source=Google&utm_medium=ppc&utm_campaign=IOT_Paidsearch_GenericProduct_AlwaysOn&utm_term=here&gclid=CjwKCAjwgr6TBhAGEiwA3aVuIY1CRORTgk0unKxtKKkhrgw_RKmlufIqzw2Pu_oz-oBbnB8SDKgg6hoCUzkQAvD_BwE&gclsrc=aw.ds"
          target="_blank"
          rel="noreferrer"
        >
          Here
        </a>
        . La metodología para realizar este minado puede consultarse{" "}
        <a
          href="http://www.metodologiaminado.com/"
          target="_blank"
          rel="noreferrer"
        >
          aquí
        </a>
        .
      </span>
    );
  },
};

export const migNews = {
  boxTitle: "Porcentaje de noticias sobre migración",
  dataSubtitle: "Porcentaje de artículos sobre migración",
  description: (isMostRecentYear, time, value, change) => {
    let comparisonSentence;
    comparisonSentence = equalsZero(change)
      ? "Esto implica que no hubo un cambio significativo respecto al " +
        (isMostRecentYear ? "mes" : "año") +
        " anterior."
      : "Esto significó un cambio con respecto al " +
        (isMostRecentYear ? "mes" : "año") +
        " anterior de " +
        (change ?? "--") +
        "pp.";

    return (
      <span>
        A fin de tener una medida de la importancia noticiosa del tema
        migratorio, se presenta el porcentaje de noticias sobre migración para
        la región. En{" "}
        {isMostRecentYear ? getMonthText(time, "es", true) : "el " + time} las
        noticias relacionadas con el tema migratorio representaron un{" "}
        {value ? formatNumber(value) : "--"}%. {comparisonSentence}
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        La fuente de información de las noticias relacionadas con la migración es 
       {" "}
        <a
          href="https://newsapi.org/"
          target="_blank"
          rel="noreferrer"
        >
          NewsAPI
        </a>
        {" "}de EventRegistry. Este recurso rastrea y captura las fuentes de información más relevantes de cada país en todo momento. En el proceso de detectar eventos, NewsAPI mina, traduce, procesa, clasifica los artículos en categorías y extrae conceptos, que podrían ser personas, organizaciones y no entidades. Con la
        {" "}
        <a
          href="https://newsapi.org/docs"
          target="_blank"
          rel="noreferrer"
        >
          NewsAPI
        </a>{" "}
        se pueden filtrar aquellos artículos que contengan conceptos relacionados con la migración humana. Con ello se obtiene el agregado mensual.
      </span>
    );
  },
};

export const principalTopics = {
  boxTitle: (isXeno) => {
    return isXeno
      ? "Temas principales en conversación xenófoba en Twitter"
      : "Temas principales en Twitter sobre migración";
  },

  dataSubtitle: "Tema más frecuente",
  description: (isXeno, year, month, topic, percentage) => {
    let timePeriodText = "";

    if (month !== "ALL") {
      timePeriodText += "durante el " + getMonthText(month, "es") + " del ";
    } else {
      timePeriodText += "en el ";
    }

    timePeriodText += year;

    return isXeno ? (
      <span>
        Al igual que para el análisis de todos los tweets sobre migración, se
        analizó la estructura temática de los tweets clasificados como
        xenófobos, utilizando las mismas categorías arriba mencionadas.
      </span>
    ) : (
      <span>
        Usando un modelo que identifica contenidos temáticos en la conversación,
        se clasificaron los tweets de acuerdo a diferentes categorias. El
        principal tema de conversación {timePeriodText} fue{" "}
        {topic ? topic : "--"}, que representó{" "}
        {percentage ? formatNumber(percentage) : "--"}% de la conversación
        total.
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        Para clasificar temáticamente los tweets, se entrenó, de forma
        semisupervisada, un modelo de agrupamiento conocido como{" "}
        <a
          href="https://maartengr.github.io/BERTopic/index.html"
          target="_blank"
          rel="noreferrer"
        >
          BERTopic
        </a>
        . El conjunto de entrenamiento constó de 292 mil textos seleccionados
        aleatoriamente dentro del periodo de tiempo de 2017 a 2022. Para evitar
        un sesgo temático, la muestra aleatoria constó de aproximadamente 13,695
        textos para los 18 países con mayor cantidad de tweets, mientras que
        para los ocho países con menor presencia se tomó la cantidad de tweets
        disponibles. Se añadió a la muestra aleatoria un total de 24 mil textos
        etiquetados con los temas COVID-19, Empleo, Género, Seguridad y
        Servicios Públicos. Puesto que BERTopic produce grupos de textos
        semánticamente similares, éste es capaz de hallar temas diferentes a los
        definidos por los datos etiquetados. El modelo resultante detectó un
        total de 271 grupos, los cuales fueron analizados manualmente a modo de
        obtener una relación entre conjuntos de grupos y las temáticas de
        interés. Los porcentajes mostrados fueron calculados a partir de la
        cantidad de textos que pertenecen a cada temática divididos por el total
        de tweets que pertenecen a alguno de los 271 grupos.
      </span>
    );
  },
};

export const convTransformation = {
  boxTitle:
    "Evolución de la conversación sobre migración en Twitter en el tiempo",
  dataSubtitle: (currentCountry, selectedTopic, topicOptions) => {
    return currentCountry === "LAC"
      ? "Porcentaje de la conversación en Twitter que habla sobre " +
          (topicOptions && topicOptions.find((t) => t.code === selectedTopic)
            ? topicOptions.find((t) => t.code === selectedTopic).label
            : "--") +
          " en América Latina y el Caribe"
      : "Porcentaje de la conversación en Twitter sobre el tema más frecuente";
  },
  description: () => {
    return (
      <span>
        Usando el mismo modelo para clasificación de datos, este gráfico analiza
        la evolución sobre el tiempo de los temas.
      </span>
    );
  },
  methodology:
    "Utilizando el mismo modelo de clasificación antes descrito, se asignaron los temas observados en cada punto del tiempo, y con esta información  se grafica la estructura porcentual temática a lo largo del tiempo. Para la serie histórica se presenta una agrupación trimestral. De otra manera, la agrupación es mensual.",
};

export const cityActivity = {
  boxTitle: "Tweets migratorios por ciudad",

  dataSubtitle: (dataType) => {
    let text;

    switch (dataType) {
      case "all":
        text =
          "Ciudad con los usuarios más activos en Twitter como tasa por cada 10,000 habitantes";
        break;
      case "xeno":
        text =
          "Ciudad con mayor actividad xenófoba en Twitter como tasa por cada 10,000 habitantes";
        break;
      case "all_volume":
        text = "Ciudad con más actividad en Twitter";
        break;
      case "xeno_volume":
        text = "Ciudad con más actividad xenófoba en Twitter";
        break;
      default:
        text = "";
    }

    return text;
  },
  description: `La actividad en la conversación sobre migración está concentrada en las principales ciudades pero varía de una ciudad a otra. Este mapa muestra la actividad total en cada ciudad (como cantidad total de tweets), desagregada por su contenido xenófobo. Para comparar ciudades sin tomar en cuenta su población, la actividad también se presenta como tasa por cada 10,000 habitantes.`,
  methodology: () => {
    return (
      <span>
        Una vez que se asignó una geografía (ciudad y país) como se describe en
        la sección de “volumen de conversación”, se contabilizaron los tweets
        tanto por país como por ciudad. En el caso de ciudades, se
        contabilizaron los tweets dentro de los polígonos de áreas urbanas
        definidos por{" "}
        <a
          href="https://earthworks.stanford.edu/catalog/stanford-yk247bg4748"
          target="_blank"
          rel="noreferrer"
        >
          Kelso, N.V. and Patterson, T. (2012). World Urban Areas, LandScan,
          1:10 million (2012)
        </a>
        . Estas áreas urbanas privilegian su importancia regional y poblacional
        sobre los límites administrativos. Para obtener las tasas de tweets por
        cada 10,000 habitantes, se utilizó la estimación poblacional de esta
        fuente de información, que estima la población de áreas urbanas
        utilizando un ráster poblacional proveniente del Oak Ridge National
        Laboratory's LandScan. Los datos que muestran la tasa de tweets
        xenófobos sólo considera ciudades de más de 100 mil habitantes en donde
        hubo más de 10 tweets en ese trimestre.
      </span>
    );
  },
  resources: {
    1: "",
  },
};

export const migratorySentiment = {
  boxTitle: "Sentimiento migratorio en el tiempo",
  dataSubtitle: "Porcentaje de conversación positiva",
  description: (
    isMostRecentYear,
    time,
    value,
    negValue,
    posChange,
    negChange
  ) => {
    let comparisonText =
      "Con respecto al " + (isMostRecentYear ? "mes" : "año") + " anterior, ";

    if (equalsZero(posChange) && equalsZero(negChange)) {
      comparisonText +=
        "ni la conversación positiva, ni la xenófoba, cambiaron.";
    } else {
      comparisonText += "la conversación positiva ";
      if (equalsZero(posChange)) {
        comparisonText += "no cambió";
      } else {
        comparisonText += posChange > 0 ? "aumentó " : "disminuyó ";
        comparisonText += posChange + "pp";
      }

      comparisonText += ", mientras que la conversación xenófoba ";

      if (equalsZero(negChange)) {
        comparisonText += "no cambió.";
      } else {
        comparisonText += negChange > 0 ? "aumentó " : "disminuyó ";
        comparisonText += negChange + "pp.";
      }
    }

    return isMostRecentYear ? (
      <span>
        Al considerar todos los tweets, la conversación positiva en{" "}
        {time ? getMonthText(time, "es", true) : "--"} representó{" "}
        {value ? formatNumber(value) : "--"}%, mientras que la xenófoba
        represento {negValue ? formatNumber(negValue) : "--"}%. {comparisonText}
      </span>
    ) : (
      <span>
        Al considerar todos los tweets, la conversación positiva en el{" "}
        {time ?? "--"} representó {value ? formatNumber(value) : "--"}%,
        mientras que la xenófoba representó{" "}
        {negValue ? formatNumber(negValue) : "--"}%.
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        <p>
          Un análisis de sentimiento clasifica los tweets como 'positivos' o
          'neutros', dependiendo de su polaridad o tono emocional, y nos brinda
          una idea de la manera en que los ciudadanos expresan opiniones sobre
          el tema migratorio a lo largo del tiempo. Regularmente, por el tono o
          uso de palabras con el que se expresa, un tweet con sentimiento o
          polaridad positiva tenderá a tener contenidos que estén a favor de los
          migrantes, y lo opuesto ocurre con tweets clasificados como xenófobos.
          Los tweets neutrales no expresan algún tono en su polaridad, como es
          el caso de muchas noticias con comentarios.
        </p>
        <p>
          Para clasificar el sentimiento de los tweets migratorios, se utilizó
          el modelo RoBERTuito, un modelo pre-entrenado BERT del repositorio de
          modelos Hugging Face, entrenado para realizar una clasificación de
          sentimiento. La elección de este modelo obedece a la similitud del
          problema que se quiere modelar. Con una base de datos etiquetada
          propia del BID, se hizo un fine-tunning del modelo. La descripción
          completa de la metodología y evaluación puede consultarse{" "}
          {/* <a
            href="https://es.overleaf.com/project/621669b06ad8e74c5b4366e1"
            target="_blank"
            rel="noreferrer"
          >
            aquí
          </a> */}
          .
        </p>
      </span>
    );
  },
};

export const migratorySentimentbyTopic = {
  boxTitle: "Sentimiento migratorio en Twitter por tema",
  dataSubtitle: "Porcentaje de conversación positiva",
  description: (
    selectedTopic,
    isMostRecentYear,
    time,
    value,
    negValue,
    posChange,
    negChange
  ) => {
    let comparisonText =
      "Con respecto al " + (isMostRecentYear ? "mes" : "año") + " anterior, ";

    if (equalsZero(posChange) && equalsZero(negChange)) {
      comparisonText +=
        "ni la conversación positiva, ni la xenófoba, cambiaron.";
    } else {
      comparisonText += "la conversación positiva ";
      if (equalsZero(posChange)) {
        comparisonText += "no cambió";
      } else {
        comparisonText += posChange > 0 ? "aumentó " : "disminuyó ";
        comparisonText += posChange + "pp";
      }

      comparisonText += ", mientras que la conversación xenófoba ";

      if (equalsZero(negChange)) {
        comparisonText += "no cambió.";
      } else {
        comparisonText += negChange > 0 ? "aumentó " : "disminuyó ";
        comparisonText += negChange + "pp.";
      }
    }

    return isMostRecentYear ? (
      <span>
        Al considerar todos los tweets relacionados con {selectedTopic}, la
        conversación positiva en {time ? getMonthText(time, "es", true) : "--"}{" "}
        representó {value ? formatNumber(value) : "--"}%, mientras que la
        xenófoba represento {negValue ? formatNumber(negValue) : "--"}%.{" "}
        {comparisonText}
      </span>
    ) : (
      <span>
        Al considerar todos los tweets relacionados con {selectedTopic}, la
        conversación positiva en el {time ?? "--"} representó{" "}
        {value ? formatNumber(value) : "--"}%, mientras que la xenófoba
        representó {negValue ? formatNumber(negValue) : "--"}%.
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        <p>
          Un análisis de sentimiento clasifica los tweets como 'positivos' o
          'neutros', dependiendo de su polaridad o tono emocional, y nos brinda
          una idea de la manera en que los ciudadanos expresan opiniones sobre
          el tema migratorio a lo largo del tiempo. Regularmente, por el tono o
          uso de palabras con el que se expresa, un tweet con sentimiento o
          polaridad positiva tenderá a tener contenidos que estén a favor de los
          migrantes, y lo opuesto ocurre con tweets clasificados como xenófobos.
          Los tweets neutrales no expresan algún tono en su polaridad, como es
          el caso de muchas noticias con comentarios.
        </p>
        <p>
          Para clasificar el sentimiento de los tweets migratorios, se utilizó
          el modelo RoBERTuito, un modelo pre-entrenado BERT del repositorio de
          modelos Hugging Face, entrenado para realizar una clasificación de
          sentimiento. La elección de este modelo obedece a la similitud del
          problema que se quiere modelar. Con una base de datos etiquetada
          propia del BID, se hizo un fine-tunning del modelo. La descripción
          completa de la metodología y evaluación puede consultarse{" "}
          {/* <a
            href="https://es.overleaf.com/project/621669b06ad8e74c5b4366e1"
            target="_blank"
            rel="noreferrer"
          >
            aquí
          </a> */}
          .
        </p>
      </span>
    );
  },
};

export const comparativeMapSent = {
  boxTitle: (selectedSentiment) =>
    "Mapa comparativo de sentimiento migratorio en Twitter " +
    (selectedSentiment === "positive" ? "positivo" : "neutral"),
  dataSubtitle: (country, selectedSentiment) =>
    `Registró el mayor porcentaje de tweets ` +
    (selectedSentiment === "positive" ? "positivos" : "neutros"),
  description:
    "Este mapa presenta una comparación de sentimiento en un corte del tiempo para todos los países analizados.",
  methodology: () => {
    return (
      <span>
        Un análisis de sentimiento clasifica los tweets como 'positivos' o
        'neutros',dependiendo de su polaridad o tono emocional, y nos brinda una
        idea de la manera en que los ciudadanos expresan opiniones sobre el tema
        migratorio a lo largo del tiempo. Regularmente, por el tono o uso de
        palabras con el que se expresa, un tweet con sentimiento o polaridad
        positiva tenderá a tener contenidos que estén a favor de la población
        migrante; lo opuesto ocurre con tweets clasificados como xenófobos. Los
        tweets neutrales no expresan algún tono en su polaridad, como es el caso
        de muchas noticias con comentarios. Para clasificar el sentimiento de
        los tweets migratorios, se utilizó el{" "}
        <a
          href="https://huggingface.co/pysentimiento/robertuito-sentiment-analysis"
          target="_blank"
          rel="noreferrer"
        >
          modelo RoBERTuito
        </a>
        , un modelo pre-entrenado BERT del repositorio de modelos Hugging Face,
        entrenado para realizar una clasificación de sentimiento. La elección de
        este modelo obedece a la similitud del problema que se quiere modelar.
        Con una base de datos etiquetada propia del BID, se hizo un fine-tuning
        del modelo. La descripción completa de la metodología y evaluación puede
        consultarse{" "}
        {/* <a
          href="https://es.overleaf.com/project/621669b06ad8e74c5b4366e1"
          target="_blank"
          rel="noreferrer"
        >
          aquí
        </a> */}
        .
      </span>
    );
  },
};

export const xenoPrevalence = {
  boxTitle: "Conversación xenófoba en Twitter",
  dataSubtitle: "Porcentaje de tweets xenófobos respecto a tweets migratorios",
  description: (isMostRecentYear, time, data, lacData, isLAC) => {
    let comparison;

    if (parseInt(data) < parseInt(lacData)) {
      comparison = "menor";
    } else {
      comparison = "mayor";
    }
    return isLAC ? (
      <span>
        Prevalencia de xenofobia se obtiene al dividir el número de tweets con
        contenido xenófobo entre el número de tweets migratorios en un periodo
        determinado. En{" "}
        {isMostRecentYear ? getMonthText(time, "es") : "el " + time}, las
        expresiones xenófobas en la conversación sobre migración representaron{" "}
        {data ? formatNumber(data) : "--"}%.
      </span>
    ) : (
      <span>
        Prevalencia de xenofobia se obtiene al dividir el número de tweets con
        contenido xenófobo entre el número de tweets migratorios en un periodo
        determinado. En{" "}
        {isMostRecentYear ? getMonthText(time, "es", true) : "el " + time}, las
        expresiones xenófobas en la conversación sobre migración representaron{" "}
        {data ? formatNumber(data) : "--"}%, que es {comparison} al nivel
        observado en la región completa (
        {lacData ? formatNumber(lacData) : "--"}%).
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        Para clasificar un tweet como xenófobo, se utilizó el{" "}
        <a
          href="https://huggingface.co/pysentimiento/robertuito-sentiment-analysis"
          target="_blank"
          rel="noreferrer"
        >
          modelo RoBERTuito
        </a>
        , un modelo pre-entrenado BERT del repositorio de modelos Hugging Face,
        entrenado para realizar una clasificación de discurso de odio. La
        elección de este modelo obedece a la similitud del problema que se
        quiere modelar. El fine-tuning del modelo se realizó con un conjunto de
        tweets etiquetados como xenófobos. Para más detalles sobre este modelo y
        sus métricas de evaluación, favor de consultar{" "}
        <a
          href="https://drive.google.com/file/d/1E-2Geoa_Ll-7PdkgkVJWOZCuU54TPqAK/view"
          target="_blank"
          rel="noreferrer"
        >
          aquí
        </a>
        .
      </span>
    );
  },
};

export const reach = {
  boxTitle: "Alcance potencial de tweets migratorios",
  dataSubtitle: "Número de cuentas alcanzadas con tweets positivos",
  description: (isMostRecentYear, time, data, negData) => {
    return (
      <span>
        Se estimó el alcance promedio por tweet al dividir el número de
        seguidores cuyas cuentas publicaron tweets positivos o xenófobos, entre
        la cantidad de tweets positivos (o xenófobos) en un periodo. En{" "}
        {isMostRecentYear ? getMonthText(time, "es", true) : "el " + time}, un
        tweet positivo tuvo un alcance potencial de{" "}
        {data ? formatNumber(data) : "--"} cuentas, mientras que un tweet
        xenófobo tuvo un alcance potencial de{" "}
        {negData ? formatNumber(negData) : "--"} cuentas.
      </span>
    );
  },
  methodology:
    "El alcance de tweets promedio se estima como la suma de los seguidores de todas las cuentas que realizaron un tweet con contenido positivo o xenófobo, dividido por la cantidad de tweets que publicaron dichas cuentas. El alto alcance de tweets positivos se explica por la publicación de una mayor cantidad de tweets positivos, así como porque existen muchas cuentas con un número muy grande de seguidores que publican contenidos positivos.",
};

//2.3 Reacción tweets de cuentas migratorias institucionales
export const reactionMigAccounts = {
  boxTitle: "Reacción a tweets de cuentas migratorias institucionales",
  dataSubtitle: (isMostRecentYear) => {
    return isMostRecentYear
      ? "Porcentaje de reacciones positivas"
      : "Promedio mensual de porcentaje de reacciones positivas";
  },
  description:
    "Para cada país se minó un conjunto de cuentas institucionales (institutos de migración, consulados, centros de investigación) que producen contenidos sobre el tema. Las reacciones que generaron (i.e. respuestas) fueron clasificadas de acuerdo a su sentimiento. ",
  methodology:
    "Cada respuesta a un tweet migratorio fue analizada con el clasificador de sentimiento descrito anteriormente. Una vez clasificada, se hizo una agrupación por su sentimiento. El modelo de clasificación de sentimiento es el mismo que se describió anteriormente.",
};

export const comparativeMapXeno = {
  boxTitle: "Mapa comparativo de prevalencia de xenofobia en Twitter",
  dataSubtitle: "País con menor prevalencia de xenofobia",
  description:
    "El mapa presenta una comparación de la prevalencia de xenofobia en un corte del tiempo para todos los países analizados.",
  methodology:
    "La clasificación de tweets xenófobos  se realizó utilizando un modelo como el descrito anteriormente. Para mayor detalle favor de consultar XXX.",
};

export const xenoAccounts = {
  boxTitle: "Cuentas xenófobas en Twitter",
  dataSubtitle: "Porcentaje de cuentas xenófobas",
  description: (isMostRecentYear, time, change) => {
    let comparisonText =
      "En " + (isMostRecentYear ? getMonthText(time, "es") : time) + " ";

    if (equalsZero(change)) {
      comparisonText += "no hubo ningún cambio ";
    } else {
      let changeText = "hubo ";
      if (change) {
        changeText += change > 0 ? "un aumento" : "una disminución";
      } else {
        changeText += "--";
      }

      comparisonText += changeText + " de " + (change ?? "--") + "pp";
    }

    comparisonText +=
      " en cuentas xenófobas con respecto al " +
      (isMostRecentYear ? "mes " : "año ") +
      "anterior.";

    return (
      <span>
        El gráfico de porcentaje de cuentas presenta el porcentaje de cuentas
        que publicaron contenidos xenófobos en relación a las cuentas que
        publicaron sobre migración. {comparisonText}
      </span>
    );
  },
  methodology:
    "Una cuenta se clasifica como xenófoba cuando ésta publica una cantidad de tweets xenófobos mayor que al promedio de tweets xenófobos por usuario en ese mes, en esa geografía. Una vez clasificadas estas cuentas, se divide su número entre el total de cuentas que publicaron algún contenido migratorio.",
};

export const xenoReach = {
  boxTitle: "Alcance de tweets xenófobos",
  dataSubtitle: "Número de cuentas alcanzadas con tweets xenófobos",
  description: (month, reach, change) => {
    return (
      <span>
        En {month || "enero"}, los contenidos xenófobos tuvieron un alcance
        potencial de {formatNumber(reach) || "15,033"} cuentas, lo que
        representó {change < 0 ? "una disminución" : " un incremento"} de{" "}
        {change ? formatNumber(change) : "--"}%
      </span>
    );
  },
  methodology:
    "El alcance de tweets xenófobos se estimó como  la suma de los seguidores de todas las cuentas únicas que realizaron un tweet con contenido migratorio. Esta estimación se realizó para tweets clasificados como positivos o xenófobos.",
};

export const perceptionsEMV = {
  boxTitle: "Encuesta Mundial de Valores",
  dataSubtitle: "",
  description:
    "La Encuesta Mundial de Valores (EMV) es un proyecto global de investigación sobre opinión pública de la mayoría de la población mundial. En esta sección se puede consultar una batería de preguntas en lo referente a actitudes hacia migrantes en los países de Latinoamérica, y analizar las respuestas haciendo cortes sociodemográficos.",
  methodology:
    "Se analizaron tres waves o cortes disponibles. No todos los países aparecen en las tres waves. Las estimaciones se realizaron utilizando el factor de expansión provisto en la encuesta.",
};

export const perceptionsLAPOP = {
  boxTitle: "Barómetro de las Americas (LAPOP)",
  dataSubtitle: "",
  description:
    "El Barómetro de las Américas, el proyecto de investigación de encuestas más conocido de LAPOP, es un estudio periódico de 34 países del hemisferio occidental, con muestras estratificadas representativas a nivel nacional en cada país, un cuestionario común y módulos específicos de cada país. Es la única encuesta comparativa científicamente rigurosa de valores y comportamientos democráticos que cubre a todos los países independientes de América del Norte, Centroamérica y Sudamérica, así como a un número significativo de países del Caribe.",
  methodology: "",
};

export const perceptionsLatino = {
  boxTitle: "Latinobarómetro",
  dataSubtitle: "",
  description:
    "El Latinobarómetro es un estudio de opinión pública realizado cada año en 18 países de América Latina. De manera similar a la EMV, se seleccionaron las preguntas relacionadas con la actitud de los latinoamericanos hacia los migrantes, y se pueden realizar cortes por edad, género, ocupación y otros. Los años que comprende el análisis son 2015, 2018, 2020 y 2023",
  methodology:
    "Se analizaron los tres años más recientes. Las estimaciones se realizaron utilizando el factor de expansión provisto en la encuesta.",
};

export const comunidades = {
  description:
    "Para comprender las conversaciones al interior de las comunidades en Twitter y Facebook, se hicieron dos análisis de comunidades. Por un lado, se analizaron las conversaciones de grupos públicos de Facebook. En segundo lugar, se identificaron las cuentas de Twitter que conversan entre ellas y forman comunidades orgánicas.",
};

export const facebookMembers = {
  boxTitle: "Grupos de migrantes en Facebook",
  dataSubtitle: "Grupo con más miembros",
  description:
    "Se analizaron grupos públicos en Facebook de migrantes y se ordenaron por cantidad de miembros.",
  methodology: "",
};

export const facebookWordcloud = {
  boxTitle: "Palabras clave en grupos de Facebook",
  dataSubtitle: "Palabra más utilizada",
  description:
    "Analizar los posts y las respuestas a dichos posts en las páginas de grupos nos ayuda a entender si estas plataformas se utilizan para generar mecanismos de coordinación o ayuda al interior de dichos grupos. Aquí se presenta un wordcloud con las palabras más utilizadas en las conversaciones.",
  methodology: "",
};

export const twitterWordcloud = {
  boxTitle: "Hashtags clave en comunidades de Twitter",
  dataSubtitle: "Hashtag más utilizado",
  description:
    "Se identificaron 12 comunidades principales utilizando un algoritmo de detección de comunidades. Para cada comunidad, se analizaron los hashtags más utilizados.",
  methodology:
    "Utilizando el algoritmo de detección de comunidades de  Blondel et al. (2008), se creó un grafo e  identificaron las comunidades más importantes de las conversaciones sobre migración. Para más información, consultar (REPORTE).",
};
